import apiClient from './client';
import { uploadDirectToOss } from './direct-upload';
import { keysToCamelCase } from '@/lib/api-utils';
import type {
  DigitalHuman,
  DigitalHumanCreateFormData,
  DigitalHumanCreateRequest,
  DigitalHumanListParams,
  DigitalHumanListResponse,
  DigitalHumanUpdateFormData,
  DigitalHumanUpdateRequest,
  DigitalHumanVerificationCreditPolicy,
  DigitalHumanVerificationRequest,
  DigitalHumanVerificationResponse,
  DigitalHumanVerificationStatusResponse,
} from '@/types/digital-human';

interface DigitalHumanMutationOptions {
  projectId?: string | null;
}

function buildMutationParams(options?: DigitalHumanMutationOptions) {
  return options?.projectId ? { projectId: options.projectId } : undefined;
}

interface UploadedDigitalHumanPrimaryImage {
  storagePath?: string;
  fileUrl?: string;
}

async function uploadPrimaryImageFile(file: File): Promise<string> {
  const result = await uploadDirectToOss<UploadedDigitalHumanPrimaryImage>({
    file,
    businessType: 'digital_human_primary_image',
    businessPayload: {},
  });
  return result.storagePath ?? result.fileUrl ?? '';
}

async function buildDigitalHumanCreateJson(
  data: DigitalHumanCreateFormData
): Promise<DigitalHumanCreateRequest> {
  const primaryImageUrl = await uploadPrimaryImageFile(data.primaryImageFile);
  return {
    name: data.name,
    description: data.description,
    primaryImageUrl,
    gender: data.gender,
    projectId: data.projectId,
    isProjectRestricted: data.isProjectRestricted,
  } as DigitalHumanCreateRequest;
}

async function buildDigitalHumanUpdateJson(
  data: DigitalHumanUpdateFormData
): Promise<DigitalHumanUpdateRequest> {
  const payload: Record<string, unknown> = {};
  if (data.name != null) payload.name = data.name;
  if (data.description != null) payload.description = data.description;
  if (data.primaryImageFile != null) {
    payload.primaryImageUrl = await uploadPrimaryImageFile(data.primaryImageFile);
  }
  if (data.gender != null) payload.gender = data.gender;
  if (data.isProjectRestricted != null) payload.isProjectRestricted = data.isProjectRestricted;
  if (data.availabilityScope != null) payload.availabilityScope = data.availabilityScope;
  if (data.boundProjectIds != null) payload.boundProjectIds = data.boundProjectIds;
  return payload as DigitalHumanUpdateRequest;
}

export const digitalHumansApi = {
  async list(params?: DigitalHumanListParams): Promise<DigitalHumanListResponse> {
    const response = await apiClient.get('/digital-humans', {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        verificationStatus: params?.verificationStatus,
        verificationStage: params?.verificationStage,
        projectId: params?.projectId,
        search: params?.search,
      },
    });
    return keysToCamelCase<DigitalHumanListResponse>(response);
  },

  async getById(digitalHumanId: string): Promise<DigitalHuman> {
    const response = await apiClient.get(`/digital-humans/${digitalHumanId}`);
    return keysToCamelCase<DigitalHuman>(response);
  },

  async create(
    data: DigitalHumanCreateRequest | DigitalHumanCreateFormData
  ): Promise<DigitalHuman> {
    const payload = 'primaryImageFile' in data ? await buildDigitalHumanCreateJson(data) : data;
    const response = await apiClient.post('/digital-humans', payload);
    return keysToCamelCase<DigitalHuman>(response);
  },

  async update(
    digitalHumanId: string,
    data: DigitalHumanUpdateRequest | DigitalHumanUpdateFormData,
    options?: DigitalHumanMutationOptions
  ): Promise<DigitalHuman> {
    const payload = 'primaryImageFile' in data ? await buildDigitalHumanUpdateJson(data) : data;
    const response = await apiClient.patch(`/digital-humans/${digitalHumanId}`, payload, {
      params: buildMutationParams(options),
    });
    return keysToCamelCase<DigitalHuman>(response);
  },

  async delete(digitalHumanId: string, options?: DigitalHumanMutationOptions): Promise<void> {
    await apiClient.delete(`/digital-humans/${digitalHumanId}`, {
      params: buildMutationParams(options),
    });
  },

  async getVerificationCreditPolicy(): Promise<DigitalHumanVerificationCreditPolicy> {
    const response = await apiClient.get('/digital-humans/verification/credit-policy');
    return keysToCamelCase<DigitalHumanVerificationCreditPolicy>(response);
  },

  async startVerification(
    digitalHumanId: string,
    data?: DigitalHumanVerificationRequest
  ): Promise<DigitalHumanVerificationResponse> {
    const response = await apiClient.post(`/digital-humans/${digitalHumanId}/verification`, data);
    return keysToCamelCase<DigitalHumanVerificationResponse>(response);
  },

  async getVerificationStatus(
    digitalHumanId: string
  ): Promise<DigitalHumanVerificationStatusResponse> {
    const response = await apiClient.get(`/digital-humans/${digitalHumanId}/verification/status`);
    return keysToCamelCase<DigitalHumanVerificationStatusResponse>(response);
  },
};
